import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    TextField,
    Button,
    Typography,
    IconButton,
    InputAdornment,
    CircularProgress,
    Alert,
    Container,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack, LockReset } from '@mui/icons-material';
import {authService} from '../services/authService';
import { toast } from 'react-toastify';
import {
    authPageStyles,
    authInputStyles,
    authButtonStyles,
    authIconButtonStyles,
    welcomeSectionStyles,
    welcomeContentStyles,
    welcomeTitleStyles,
    welcomeDescriptionStyles,
    decorativeCircle1Styles,
    decorativeCircle2Styles,
    stepperStyles,
} from '../../../assets/styles';

// Step 1: Email validation
const emailSchema = Yup.object({
    email: Yup.string()
        .email('Email không hợp lệ')
        .required('Email là bắt buộc'),
});

// Step 2: OTP validation
const otpSchema = Yup.object({
    otp: Yup.string()
        .length(6, 'Mã OTP phải có 6 ký tự')
        .required('Mã OTP là bắt buộc'),
});

// Step 3: Reset password validation
const resetPasswordSchema = Yup.object({
    newPassword: Yup.string()
        .min(6, 'Mật khẩu phải có ít nhất 6 ký tự')
        .required('Mật khẩu mới là bắt buộc'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Mật khẩu xác nhận không khớp')
        .required('Xác nhận mật khẩu là bắt buộc'),
});

const steps = ['Nhập Email', 'Xác thực OTP', 'Đặt mật khẩu mới'];

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Step 1: Send OTP to email
    const emailFormik = useFormik({
        initialValues: { email: '' },
        validationSchema: emailSchema,
        onSubmit: async (values) => {
            setError('');
            try {
                await authService.forgotPassword(values.email);
                setEmail(values.email);
                toast.success('Mã OTP đã được gửi đến email của bạn!');
                setActiveStep(1);
            } catch (err) {
                const msg = err.response?.data?.message || 'Không thể gửi mã OTP';
                setError(msg);
                toast.error(msg);
            }
        },
    });

    // Step 2: Verify OTP
    const otpFormik = useFormik({
        initialValues: { otp: '' },
        validationSchema: otpSchema,
        onSubmit: async (values) => {
            setError('');
            try {
                // Verify OTP first before going to step 3
                await authService.verifyResetOtp({ email, otp: values.otp });
                setActiveStep(2);
            } catch (err) {
                const msg = err.response?.data?.message || 'Mã OTP không hợp lệ hoặc đã hết hạn';
                setError(msg);
                toast.error(msg);
            }
        },
    });

    // Step 3: Reset password
    const resetFormik = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: resetPasswordSchema,
        onSubmit: async (values) => {
            setError('');
            try {
                // Send full payload to reset password
                await authService.resetPassword({
                    email: email,
                    otp: otpFormik.values.otp,
                    newPassword: values.newPassword,
                    confirmPassword: values.confirmPassword
                });
                toast.success('Đặt lại mật khẩu thành công! Vui lòng đăng nhập.');
                navigate('/login');
            } catch (err) {
                const msg = err.response?.data?.message || 'Không thể đặt lại mật khẩu';
                setError(msg);
                toast.error(msg);
            }
        },
    });

    const handleBack = () => {
        setError('');
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        } else {
            navigate('/login');
        }
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box component="form" onSubmit={emailFormik.handleSubmit}>
                        <TextField
                            fullWidth
                            id="email"
                            name="email"
                            label="Email"
                            placeholder="example@mail.com"
                            value={emailFormik.values.email}
                            onChange={emailFormik.handleChange}
                            error={emailFormik.touched.email && Boolean(emailFormik.errors.email)}
                            helperText={emailFormik.touched.email && emailFormik.errors.email}
                            sx={{ ...authInputStyles, mb: 3 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={emailFormik.isSubmitting}
                            startIcon={emailFormik.isSubmitting ? <CircularProgress size={20} /> : <LockReset />}
                            sx={authButtonStyles}
                        >
                            {emailFormik.isSubmitting ? 'Đang gửi...' : 'Gửi mã OTP'}
                        </Button>
                    </Box>
                );
            case 1:
                return (
                    <Box component="form" onSubmit={otpFormik.handleSubmit}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Mã OTP đã được gửi đến email: <strong>{email}</strong>
                        </Alert>
                        <TextField
                            fullWidth
                            id="otp"
                            name="otp"
                            label="Mã OTP"
                            placeholder="Nhập mã 6 số"
                            value={otpFormik.values.otp}
                            // limit input length manually to avoid deprecated inputProps usage
                            onChange={(e) => {
                                const val = e.target.value.slice(0, 6);
                                otpFormik.setFieldValue('otp', val);
                            }}
                            error={otpFormik.touched.otp && Boolean(otpFormik.errors.otp)}
                            helperText={otpFormik.touched.otp && otpFormik.errors.otp}
                            sx={{ ...authInputStyles, mb: 3 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={otpFormik.isSubmitting}
                            sx={authButtonStyles}
                        >
                            Xác thực OTP
                        </Button>
                    </Box>
                );
            case 2:
                return (
                    <Box component="form" onSubmit={resetFormik.handleSubmit}>
                        <TextField
                            fullWidth
                            id="newPassword"
                            name="newPassword"
                            label="Mật khẩu mới"
                            type={showNewPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={resetFormik.values.newPassword}
                            onChange={resetFormik.handleChange}
                            error={resetFormik.touched.newPassword && Boolean(resetFormik.errors.newPassword)}
                            helperText={resetFormik.touched.newPassword && resetFormik.errors.newPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                            sx={authIconButtonStyles}
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={authInputStyles}
                        />
                        <TextField
                            fullWidth
                            id="confirmPassword"
                            name="confirmPassword"
                            label="Xác nhận mật khẩu"
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={resetFormik.values.confirmPassword}
                            onChange={resetFormik.handleChange}
                            error={resetFormik.touched.confirmPassword && Boolean(resetFormik.errors.confirmPassword)}
                            helperText={resetFormik.touched.confirmPassword && resetFormik.errors.confirmPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                            sx={authIconButtonStyles}
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ ...authInputStyles, mb: 3 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={resetFormik.isSubmitting}
                            startIcon={resetFormik.isSubmitting ? <CircularProgress size={20} /> : <LockReset />}
                            sx={authButtonStyles}
                        >
                            {resetFormik.isSubmitting ? 'Đang xử lý...' : 'Đặt lại mật khẩu'}
                        </Button>
                    </Box>
                );

            default:
                return null;
        }
    };
    return (
        <Box sx={authPageStyles.container}>
            <Container maxWidth="md">
                <Box sx={authPageStyles.mainBox}>
                    {/* Wrap sections in a responsive row */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: 3 }}>
                        {/* BÊN TRÁI: FORM QUÊN MẬT KHẨU */}
                        <Box sx={[authPageStyles.formSection, { flex: 1 }]}>
                            {/* Header */}
                            <Box sx={authPageStyles.headerSection}>
                                <Typography variant="h4" sx={authPageStyles.logoText}>
                                    GPT Team
                                </Typography>
                                <Typography variant="h6" sx={authPageStyles.pageTitle}>
                                    Quên mật khẩu
                                </Typography>
                                <Typography variant="body2" sx={authPageStyles.subtitle}>
                                    Đừng lo lắng, chúng tôi sẽ giúp bạn khôi phục
                                </Typography>
                            </Box>

                            {/* Stepper */}
                            <Stepper activeStep={activeStep} sx={stepperStyles}>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>

                            {/* Error Alert */}
                            {error && (
                                <Alert severity="error" sx={authPageStyles.alert}>
                                    {error}
                                </Alert>
                            )}

                            {/* Step Content */}
                            {renderStepContent()}

                            {/* Back Button */}
                            <Button
                                fullWidth
                                variant="text"
                                startIcon={<ArrowBack />}
                                onClick={handleBack}
                                sx={{
                                    mt: 2,
                                    color: 'text.secondary',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                            >
                                {activeStep === 0 ? 'Quay lại đăng nhập' : 'Quay lại bước trước'}
                            </Button>
                        </Box>

                        {/* BÊN PHẢI: WELCOME SECTION */}
                        <Box sx={[welcomeSectionStyles, { flex: 1 }]}>
                            <Box sx={welcomeContentStyles}>
                                <Typography variant="h4" sx={welcomeTitleStyles}>
                                    Khôi phục<br />tài khoản
                                </Typography>
                                <Typography variant="h6" sx={welcomeDescriptionStyles}>
                                    Chỉ cần vài bước đơn giản để khôi phục quyền truy cập vào tài khoản của bạn.
                                    An toàn và bảo mật luôn là ưu tiên hàng đầu.
                                </Typography>
                            </Box>
                            
                            {/* Decorative circles */}
                            <Box sx={decorativeCircle1Styles} />
                            <Box sx={decorativeCircle2Styles} />
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default ForgotPasswordPage;
